import React, { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import Tag from "../../../../ui-components/molecules/tag/tag";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { Routes } from "../../../../app/route/RoutesConfig";
import { AttemptSummary, FactoringAttemptStep } from "../../../../app/data/invoice-factoring/models";
import {
  ATTEMPT_DOCUMENT_OUTCOME_COLORS,
  ATTEMPT_DOCUMENT_OUTCOME_LABELS,
  ATTEMPT_OUTCOME_COLORS,
  ATTEMPT_OUTCOME_LABELS,
  ATTEMPT_STEP_LABELS,
  formatDateTime,
} from "../constants";

type AttemptLike = AttemptSummary & { batch?: { id: string; batchNumber: number } | null };

export interface AttemptStepsProps<T extends AttemptLike> {
  attempts: T[];
  emptyText?: string;
  newestFirst?: boolean;
  showDocumentSentAt?: boolean;
  terminalPrefix?: string;
  renderDetails?: (attempt: T) => ReactNode;
}

const STEP_ORDER: FactoringAttemptStep[] = ["JOB_CREATION", "DOCUMENT_UPLOAD"];

interface AttemptCardProps<T extends AttemptLike> {
  attempt: T;
  showDocumentSentAt: boolean;
  terminalPrefix?: string;
  renderDetails?: (attempt: T) => ReactNode;
}

function AttemptCard<T extends AttemptLike>({ attempt, showDocumentSentAt, terminalPrefix, renderDetails }: AttemptCardProps<T>) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`xgs-invoice-factoring__attempt xgs-invoice-factoring__attempt--${attempt.outcome.toLowerCase()}`}>
      <div className="xgs-invoice-factoring__attempt-head">
        <span className="xgs-invoice-factoring__attempt-step">{ATTEMPT_STEP_LABELS[attempt.step]}</span>
        <Tag mods={{ color: ATTEMPT_OUTCOME_COLORS[attempt.outcome], uppercase: false }}>
          {ATTEMPT_OUTCOME_LABELS[attempt.outcome]}
        </Tag>
        {attempt.responseStatus !== null && (
          <span className="xgs-invoice-factoring__muted">HTTP {attempt.responseStatus}</span>
        )}
        {attempt.retryCount > 0 && (
          <span className="xgs-invoice-factoring__muted">
            {attempt.retryCount} {attempt.retryCount === 1 ? "retry" : "retries"}
          </span>
        )}
        <span className="xgs-invoice-factoring__muted">
          {formatDateTime(attempt.startedAt)} by {attempt.triggeredBy}
        </span>
        {attempt.batch && terminalPrefix !== undefined && (
          <Link
            className="xgs-table-link-cell"
            to={`${terminalPrefix}${Routes.billing.invoiceFactoringBatches}/${attempt.batch.id}`}
          >
            Batch #{attempt.batch.batchNumber}
          </Link>
        )}
      </div>
      {attempt.errorMessage && <div className="xgs-invoice-factoring__text--error">{attempt.errorMessage}</div>}
      {attempt.mergedFileName && (
        <div className="xgs-invoice-factoring__muted">Merged file: {attempt.mergedFileName}</div>
      )}
      {attempt.documents.length > 0 && (
        <table className="xgs-invoice-factoring__doc-table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Source ID</th>
              <th>Result</th>
              {showDocumentSentAt && <th>First sent</th>}
            </tr>
          </thead>
          <tbody>
            {attempt.documents.map(({ document, outcome }) => (
              <tr key={document.id}>
                <td>{document.fileName || "—"}</td>
                <td className="xgs-invoice-factoring__mono">{document.sourceDocumentId}</td>
                <td>
                  <Tag mods={{ color: ATTEMPT_DOCUMENT_OUTCOME_COLORS[outcome], uppercase: false }}>
                    {ATTEMPT_DOCUMENT_OUTCOME_LABELS[outcome]}
                  </Tag>
                </td>
                {showDocumentSentAt && <td>{formatDateTime(document.sentAt)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {renderDetails && (
        <>
          <Button theme={ButtonThemes.transparent} type="button" onClick={() => setOpen(!open)}>
            {open ? "Hide request and response" : "Show request and response"}
          </Button>
          {open && renderDetails(attempt)}
        </>
      )}
    </div>
  );
}

function AttemptSteps<T extends AttemptLike>({
  attempts,
  emptyText = "Nothing has been sent for this row yet.",
  newestFirst = false,
  showDocumentSentAt = true,
  terminalPrefix,
  renderDetails,
}: AttemptStepsProps<T>) {
  if (!attempts.length) return <div className="xgs-invoice-factoring__muted">{emptyText}</div>;

  const sorted = [...attempts].sort((a, b) => {
    const byTime = a.startedAt.localeCompare(b.startedAt) || STEP_ORDER.indexOf(a.step) - STEP_ORDER.indexOf(b.step);
    return newestFirst ? -byTime : byTime;
  });

  return (
    <div className="xgs-invoice-factoring__attempts">
      {sorted.map((attempt) => (
        <AttemptCard
          key={attempt.id}
          attempt={attempt}
          showDocumentSentAt={showDocumentSentAt}
          terminalPrefix={terminalPrefix}
          renderDetails={renderDetails}
        />
      ))}
    </div>
  );
}

export default AttemptSteps;
